import React, { useState } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import MentorTabs from "./MentorTabs";
import MentorOverviewTab from "./MentorOverviewTab";
import JuniorsTab from "./JuniorsTab";
import Instructions from "../../components/Instructions/Instructions";

const Wrap = styled.section`
  background: var(--nd-black);
  min-height: 100vh;
  padding: 128px 0 96px;
  color: var(--nd-text-primary);
  font-family: 'Space Grotesk', sans-serif;
`;

const Kicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 12px;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 40px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  margin: 0 0 8px;
`;

const Lede = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 720px;
  margin: 0 0 32px;
`;

const RENDERERS = {
  overview: MentorOverviewTab,
  juniors: JuniorsTab,
};

const MentorDashboardLayout = ({ tab, onTabChange, focusJuniorId: focusJuniorIdProp, onFocusConsumed }) => {
  // Local focus for the at-risk click path (onOpenJunior). The prop takes
  // precedence when set (deep-link), matching DashboardLayout's pattern of
  // passing focusLeadId straight through.
  const [localFocusJuniorId, setLocalFocusJuniorId] = useState(null);

  const handleOpenJunior = (uid) => {
    setLocalFocusJuniorId(uid);
    onTabChange("juniors");
  };

  const handleFocusConsumed = () => {
    setLocalFocusJuniorId(null);
    if (onFocusConsumed) onFocusConsumed();
  };

  // Prop (deep-link from page) wins over local at-risk click.
  const resolvedFocusJuniorId = focusJuniorIdProp || localFocusJuniorId;

  const Active = RENDERERS[tab] || MentorOverviewTab;

  return (
    <Wrap>
      <Container>
        <Kicker>MENTORSHIP</Kicker>
        <Title>Mentor Dashboard</Title>
        <Lede>
          Junior progress, streaks, and at-risk signals in one place.
        </Lede>

        <Instructions label="how the mentor dashboard works">
          <h4>Overview tab</h4>
          <p>
            Aggregate view across all juniors: XP trends, streak health, and an
            at-risk list (low streak, low lives, or not seen recently). Click any
            at-risk junior to jump straight to their drawer in the Juniors tab.
          </p>
          <h4>Juniors tab</h4>
          <p>
            Full roster — sortable by name, XP, streak, or last activity; filter
            by search. Click a row to open the per-junior drawer.
          </p>
          <h4>Per-junior drawer</h4>
          <p>
            Shows the junior&rsquo;s progress bars (path completion, labs
            verified, lessons done), key metrics (XP, streak, lives), and the
            date of their next scheduled bi-weekly check-in.
          </p>
          <h4>Comment &amp; plan editor</h4>
          <p>
            Write a private mentor note and a development plan for the junior.
            Changes are saved to <code>mentor_notes/{"{uid}"}</code> in
            Firestore. Only mentors and admins can read or write these notes.
          </p>
        </Instructions>

        <MentorTabs tab={tab} onTabChange={onTabChange} />
        <Active
          onOpenJunior={handleOpenJunior}
          focusJuniorId={resolvedFocusJuniorId}
          onFocusConsumed={handleFocusConsumed}
        />
      </Container>
    </Wrap>
  );
};

export default MentorDashboardLayout;
